import type { ApiCallbackParams } from "@similarweb/ai-kit";

import type {
    CommonRequestOptions,
    ForceResultWithFullResponse,
    IFetchService,
    PostConfigObject } from "app/services/fetchService";
import {
    DefaultFetchService,
    type GetRequestOptions
} from "app/services/fetchService";

import { API_CREDENTIALS, API_HEADERS, API_REQUEST_METHOD, MESSAGE_ENDPOINT } from "./constants";
import { logErrorToSentry, logErrorToTrackers } from "./utils";

const DEFAULT_URL = "/sse/contentStrategy/seoAgent";
const THREADS_URL = `${DEFAULT_URL}/newThread`;

export interface ISEOAGentApiParams {
    country: string;
    keywords: string[];
    main_domain: string;
    threadId?: string;
}

type Thread = {
    threadId: string;
    domain: string;
    companyName: string;
    createdDate: string;
};

const defaultFetchServiceOverride = Object.create(DefaultFetchService.getInstance());
defaultFetchServiceOverride.get = function get<R>(
    url: string,
    params?: object,
    config?: CommonRequestOptions,
): Promise<R> {
    return Object.getPrototypeOf(this).get(url, params, {
        ...config,
        preventAutoCancellation: true,
    });
};

export const aiAgent = createAIASEOAgentApiService(defaultFetchServiceOverride);

function createAIASEOAgentApiService(apiClient: IFetchService) {
    return {
        createAIAgentThreadAsync: async (options?: Readonly<PostConfigObject>): Promise<string> => {
            try {
                const credentials = ["omit", "same-origin", "include"].includes(API_CREDENTIALS)
                    ? (API_CREDENTIALS as RequestCredentials)
                    : "include";

                const response = await fetch(THREADS_URL, {
                    method: API_REQUEST_METHOD,
                    body: JSON.stringify({}),
                    credentials,
                    headers: API_HEADERS,
                });

                if (!response.ok) {
                    throw new Error(`SEO Agent/create new threadId fetch error: ${response.status}`);
                }

                const data = await response.json();
                return data.threadId; // Return just the threadId string as expected
            } catch (error) {
                throw new Error(`SEO Agent/create new threadId fetch error`);
            }
        },
        startConversation: async ({
            threadId,
            payload,
            apiCallbackParams,
        }: {
            threadId?: string;
            payload: ISEOAGentApiParams;
            apiCallbackParams: ApiCallbackParams;
        }): Promise<Response> => {
            const MAX_RETRIES = 3;
            const RETRY_DELAY_MS = 1000;
            const TIMEOUT_MS = 15000;

            async function monitorStreamTimeout(
                promise: Promise<Response>,
                firstChunkTimeoutMs: number,
                onTimeout: () => void,
            ) {
                const firstChunkTimeoutId = setTimeout(() => {
                    onTimeout();
                }, firstChunkTimeoutMs);

                function resetTimeout() {
                    clearTimeout(firstChunkTimeoutId);
                }

                try {
                    const res = await promise;
                    const clone = res.clone();
                    const body = clone.body;
                    const reader = body?.getReader();
                    if (reader) {
                        await reader.read();
                        resetTimeout();
                    }
                } finally {
                    resetTimeout();
                }
            }

            const attemptFetch = async (retryCount: number): Promise<Response> => {
                try {
                    if (retryCount > 0) {
                        const delayMs = RETRY_DELAY_MS * Math.pow(2, retryCount - 1);
                        await new Promise((resolve) => setTimeout(resolve, delayMs));
                    }

                    const url = threadId ? `${MESSAGE_ENDPOINT}?threadId=${threadId}` : MESSAGE_ENDPOINT;

                    const promise = fetch(url, {
                        method: API_REQUEST_METHOD,
                        body: JSON.stringify(payload),
                        credentials: API_CREDENTIALS,
                        headers: API_HEADERS,
                        signal: apiCallbackParams.abortSignal,
                    });

                    await monitorStreamTimeout(promise, TIMEOUT_MS, () => {
                        const ERROR_MESSAGES = `Timeout Error - Stream has timed out after ${TIMEOUT_MS}ms due to inactivity.`;
                        const error = new Error(ERROR_MESSAGES);
                        logErrorToSentry(`SEO Agent `, error);
                        logErrorToTrackers(`Timeout Error`, error);
                    });

                    const result = await promise;
                    if (!result.ok) {
                        throw new Error(`startConversation fetch error: ${result.status}`);
                    }
                    return result;
                } catch (error) {
                    if (retryCount === MAX_RETRIES - 1) {
                        throw new Error(`fetch error after ${MAX_RETRIES} retries`);
                    }
                    return attemptFetch(retryCount + 1);
                }
            };

            return attemptFetch(0);
        },
        postAIAgentMessage: (
            {
                threadId,
                message,
            }: {
                threadId: string;
                message: string;
            },
            options?: Readonly<PostConfigObject>,
        ): Promise<ForceResultWithFullResponse<string>> => {
            return apiClient.post(`${THREADS_URL}/${threadId}/message`, { message }, options);
        },
        getThreads: (options?: Readonly<GetRequestOptions>): Promise<Thread[]> => {
            return apiClient.get(`${THREADS_URL}`, undefined, options);
        },
        getThreadById: (threadId: string, options?: Readonly<GetRequestOptions>): Promise<Thread> => {
            return apiClient.get(`${THREADS_URL}/${threadId}`, undefined, options);
        },
    };
}
